import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertCircle, Check } from "lucide-react";
import { api } from "@/lib/api";
import { useContent } from "@/context/ContentContext";
import TerytAutocomplete from "@/components/TerytAutocomplete";
import ConsentCheckboxes from "@/components/ConsentCheckboxes";
import OrderSummary from "@/components/OrderSummary";
import { useHumanCheck } from "@/hooks/useHumanCheck";
import SalesLockGate from "@/components/SalesLockGate";
import PagePlacements from "@/components/PagePlacements";

const PESEL_RE = /^\d{11}$/;
const POSTAL_RE = /^\d{2}-\d{3}$/;
const PHONE_RE = /^\+48\d{9}$/;

function peselGender(pesel) {
  if (!PESEL_RE.test(pesel)) return null;
  const weights = [1, 3, 7, 9, 1, 3, 7, 9, 1, 3];
  let sum = 0;
  for (let i = 0; i < 10; i++) sum += parseInt(pesel[i], 10) * weights[i];
  const check = (10 - (sum % 10)) % 10;
  if (check !== parseInt(pesel[10], 10)) return null;
  return parseInt(pesel[9], 10) % 2 === 0 ? "K" : "M";
}

export default function PortNumber() {
  const [params] = useSearchParams();
  const { phone_plans, pages } = useContent();
  const p = pages?.port_number || {};
  const urlPlan = params.get("plan");
  const planLocked = !!urlPlan && phone_plans.some((x) => x.id === urlPlan);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    current_number: "+48",
    pesel: "",
    chosen_plan: urlPlan || phone_plans[1]?.id || phone_plans[0]?.id,
    address_street: "",
    house_number: "",
    apartment_number: "",
    address_city: "",
    address_postal: "",
    notes: "",
    teryt_simc: "",
    teryt_ulic: "",
    teryt_woj: "",
  });
  const [errors, setErrors] = useState({});
  const [simcSym, setSimcSym] = useState(null);
  const [cityHasStreets, setCityHasStreets] = useState(null);
  const [streetVerified, setStreetVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  const [consents, setConsents] = useState({ rodo: false, privacy: false, orders: false, marketing: false, electronic: false });
  const [consentError, setConsentError] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [pendingConfirm, setPendingConfirm] = useState(false);
  const human = useHumanCheck();

  const update = (k) => (e) => {
    setForm({ ...form, [k]: e.target.value });
    if (errors[k]) setErrors({ ...errors, [k]: null });
  };

  // Handler dla pól telefonu: wymusza +48, usuwa spacje, max 9 cyfr
  const updatePhone = (k) => (e) => {
    let v = e.target.value.replace(/\s+/g, "");
    if (!v.startsWith("+48")) v = "+48" + v.replace(/^\+?/, "").replace(/^48/, "");
    const digits = v.slice(3).replace(/\D/g, "").slice(0, 9);
    v = "+48" + digits;
    setForm({ ...form, [k]: v });
    if (errors[k]) setErrors({ ...errors, [k]: null });
  };

  const validate = () => {
    const errs = {};
    if (!form.first_name.trim()) errs.first_name = "Wpisz imię";
    if (!form.last_name.trim()) errs.last_name = "Wpisz nazwisko";
    if (!form.email.trim()) errs.email = "Wpisz e-mail";
    else if (!/^\S+@\S+\.\S+$/.test(form.email)) errs.email = "Nieprawidłowy adres e-mail";
    if (!form.current_number.trim() || form.current_number.trim() === "+48") errs.current_number = "Wpisz obecny numer";
    else if (!PHONE_RE.test(form.current_number.trim())) errs.current_number = "Numer musi mieć format +48XXXXXXXXX (9 cyfr, bez spacji)";
    if (!form.pesel.trim()) errs.pesel = "Wpisz PESEL (11 cyfr)";
    else if (!peselGender(form.pesel)) errs.pesel = "Nieprawidłowy PESEL";
    if (!form.address_city.trim()) errs.address_city = "Wybierz miejscowość z listy";
    // Ulica wymagana tylko jeśli miejscowość ma jakiekolwiek ulice w bazie TERYT
    // Ulica wymagana tylko jeśli miejscowość ma jakiekolwiek ulice w bazie TERYT
    if (cityHasStreets === true) {
      if (!form.address_street.trim()) {
        errs.address_street = "Wybierz ulicę z listy";
      } else if (!streetVerified) {
        errs.address_street = "Musisz wybrać ulicę z listy podpowiedzi (nie wpisuj ręcznie)";
      }
    }
    if (!form.house_number.trim()) errs.house_number = "Wpisz numer domu";
    if (!form.address_postal.trim()) errs.address_postal = "Wpisz kod pocztowy";
    else if (!POSTAL_RE.test(form.address_postal.trim())) errs.address_postal = "Format 00-000";
    return errs;
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      toast.error("Popraw zaznaczone pola");
      return;
    }
    if (!consents.rodo || !consents.privacy || !consents.orders) {
      setConsentError(true);
      toast.error("Musisz zaakceptować wymagane zgody");
      return;
    }
    setConsentError(false);
    if (!human.isValid()) {
      toast.error("Weryfikacja anty-bot nie powiodła się");
      return;
    }
    setPendingConfirm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const confirmPort = async (discountInfo) => {
    setLoading(true);
    try {
      const { data } = await api.post("/porting", {
        ...form,
        consent_rodo: consents.rodo,
        consent_privacy: consents.privacy,
        consent_orders: consents.orders,
        consent_marketing: consents.marketing,
        consent_electronic: consents.electronic,
        discount_code: discountInfo?.discount_code || null,
        discount_value: discountInfo?.discount_value || null,
        final_price: discountInfo?.final_price ?? null,
        ...human.values,
      });
      toast.success("Wniosek przyjęty!");
      setSubmitted(data.order_number);
      setPendingConfirm(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Nie udało się wysłać wniosku.");
      setLoading(false);
    }
  };

  const backToEdit = () => {
    setPendingConfirm(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const ErrorMsg = ({ name }) =>
    errors[name] && errors[name].trim() ? (
      <div className="rm-error-text" data-testid={`port-error-${name}`}>
        <AlertCircle size={12} /> {errors[name]}
      </div>
    ) : null;
  const cls = (name) => `rm-input mt-1 ${errors[name] ? "rm-input-error" : ""}`;

  return (
    <div data-testid="port-page" className="relative max-w-5xl mx-auto px-6 py-20">
      {submitted ? (
        <div data-testid="port-success" className="rm-card p-10 max-w-xl mx-auto text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
            <Check size={32} className="text-green-600" />
          </div>
          <h1 className="font-display text-3xl font-black text-slate-900 mt-6">{p.success_title || "Wniosek przyjęty!"}</h1>
          <p className="text-slate-600 mt-3">Twój numer wniosku:</p>
          <div data-testid="port-order-number" className="mt-2 text-2xl font-mono font-black text-[#1f3a8a] tracking-wider">{submitted}</div>
          <p className="text-slate-500 text-sm mt-4">{p.success_message || "Zapisz ten numer. Skontaktujemy się z Tobą, aby dokończyć proces przeniesienia numeru."}</p>
          <a href="/" className="rm-btn-primary inline-flex mt-8">Wróć na stronę główną</a>
        </div>
      ) : pendingConfirm ? (
        <OrderSummary
          testIdPrefix="port"
          plan={phone_plans.find((x) => x.id === form.chosen_plan)}
          form={form}
          consents={consents}
          loading={loading}
          onConfirm={confirmPort}
          onBack={backToEdit}
          extraRows={[{ label: "Numer do przeniesienia", value: form.current_number }]}
        />
      ) : (
      <>
      <div className="max-w-2xl">
        <div className="text-white/70 text-xs uppercase tracking-widest font-semibold">{p.eyebrow || "Przeniesienie numeru"}</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black text-white mt-3">
          {p.title || "Zatrzymaj swój numer"}
        </h1>
        <p className="text-white/70 mt-4">
          {p.subtitle || "Przenieś numer do Rmobile. Cały proces zajmuje do 24 godzin i jest darmowy."}
        </p>
      </div>

      <SalesLockGate title="Przeniesienie numeru niedostępne">
      <form onSubmit={submit} data-testid="port-form" noValidate className="rm-card p-8 mt-10 grid sm:grid-cols-2 gap-5">
        <div>
          <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">Imię *</label>
          <input data-testid="port-first-name" value={form.first_name} onChange={update("first_name")} className={cls("first_name")} />
          <ErrorMsg name="first_name" />
        </div>
        <div>
          <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">Nazwisko *</label>
          <input data-testid="port-last-name" value={form.last_name} onChange={update("last_name")} className={cls("last_name")} />
          <ErrorMsg name="last_name" />
        </div>
        <div>
          <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">Email *</label>
          <input data-testid="port-email" type="email" value={form.email} onChange={update("email")} className={cls("email")} />
          <ErrorMsg name="email" />
        </div>
        <div>
          <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">Twój obecny numer *</label>
          <input data-testid="port-number" value={form.current_number} onChange={updatePhone("current_number")} className={cls("current_number")} placeholder="+48XXXXXXXXX" maxLength={12} inputMode="tel" />
          <ErrorMsg name="current_number" />
        </div>
        <div>
          <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">Wybrany abonament *</label>
          {planLocked ? (
            (() => {
              const sel = phone_plans.find((x) => x.id === form.chosen_plan);
              return (
                <div
                  data-testid="port-plan-locked"
                  className="rm-input mt-1 flex items-center justify-between bg-slate-50 border-slate-200 cursor-not-allowed"
                >
                  <span className="font-semibold text-slate-800">
                    {sel ? `${sel.name} — ${sel.price} zł${sel.period}` : form.chosen_plan}
                  </span>
                  <span className="text-xs uppercase tracking-wider text-[#1f3a8a] bg-[#1f3a8a]/10 px-2 py-0.5 rounded-full">Wybrano</span>
                </div>
              );
            })()
          ) : (
            <select data-testid="port-plan" value={form.chosen_plan} onChange={update("chosen_plan")} className="rm-input mt-1">
              {phone_plans.map((p) => <option key={p.id} value={p.id}>{p.name} — {p.price} zł{p.period}</option>)}
            </select>
          )}
        </div>
        <div>
          <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">PESEL *</label>
          <input data-testid="port-pesel" value={form.pesel} onChange={update("pesel")} maxLength={11} className={cls("pesel")} placeholder="11 cyfr" />
          <ErrorMsg name="pesel" />
        </div>

        <div className="sm:col-span-2">
          <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">Miejscowość *</label>
          <TerytAutocomplete
            mode="simc"
            testId="port-city"
            value={form.address_city}
            error={!!errors.address_city}
            onChange={(v) => { setForm({ ...form, address_city: v }); setSimcSym(null); if (errors.address_city) setErrors({ ...errors, address_city: null }); }}
            onSelect={async (s) => {
              setForm({ ...form, address_city: s.city, address_street: "", address_postal: "", teryt_simc: s.sym, teryt_woj: s.woj_code || "", teryt_ulic: "" });
              setSimcSym(s.sym);
              setStreetVerified(false);
              setCityHasStreets(null);
              setErrors({ ...errors, address_city: null, address_street: null, address_postal: null });
              try {
                const hs = await api.get("/address/has-streets", { params: { sym: s.sym } });
                setCityHasStreets(!!hs.data?.has_streets);
              } catch { setCityHasStreets(null); }
              try {
                const res = await api.get("/address/search", { params: { q: s.city } });
                const hit = (res.data || []).find((r) => r.postal);
                if (hit?.postal) setForm((prev) => ({ ...prev, address_postal: hit.postal }));
              } catch {}
            }}
            placeholder="Wpisz nazwę miejscowości..."
          />
          <ErrorMsg name="address_city" />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
            Ulica {cityHasStreets === false
              ? <span className="text-slate-400 font-normal normal-case">(ta miejscowość nie ma ulic)</span>
              : cityHasStreets === true
              ? "*"
              : <span className="text-slate-400 font-normal normal-case">(jeśli występuje)</span>}
          </label>
          <TerytAutocomplete
            mode="ulic"
            simcSym={simcSym}
            disabled={!simcSym || cityHasStreets === false}
            testId="port-street"
            value={form.address_street}
            error={!!errors.address_street}
            onChange={(v) => { setForm({ ...form, address_street: v, teryt_ulic: "" }); setStreetVerified(false); if (errors.address_street) setErrors({ ...errors, address_street: null }); }}
            onSelect={async (s) => {
              setForm((prev) => ({ ...prev, address_street: s.street, teryt_ulic: s.sym_ul || "" }));
              setStreetVerified(true);
              setErrors({ ...errors, address_street: null });
              try {
                const res = await api.get("/address/search", { params: { q: `${s.nazwa_1 || s.street} ${form.address_city}` } });
                const hit = (res.data || []).find((r) => r.postal);
                if (hit?.postal) setForm((prev) => ({ ...prev, address_street: s.street, teryt_ulic: s.sym_ul || "", address_postal: hit.postal }));
              } catch {}
            }}
            placeholder={
              !simcSym ? "Najpierw wybierz miejscowość"
              : cityHasStreets === false ? "Ta miejscowość nie ma ulic"
              : "Wpisz nazwę ulicy..."
            }
          />
          <ErrorMsg name="address_street" />
        </div>
        <div>
          <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">Nr domu *</label>
          <input data-testid="port-house" value={form.house_number} onChange={update("house_number")} className={cls("house_number")} />
          <ErrorMsg name="house_number" />
        </div>
        <div>
          <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">Nr lokalu</label>
          <input data-testid="port-apartment" value={form.apartment_number} onChange={update("apartment_number")} className="rm-input mt-1" placeholder="opcjonalnie" />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">Kod pocztowy *</label>
          <input data-testid="port-postal" value={form.address_postal} onChange={update("address_postal")} className={cls("address_postal")} placeholder="00-000" maxLength={6} />
          <ErrorMsg name="address_postal" />
        </div>

        <div className="sm:col-span-2">
          <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">Uwagi</label>
          <textarea data-testid="port-notes" value={form.notes} onChange={update("notes")} rows={3} className="rm-input mt-1" />
        </div>
        {human.element}
        <ConsentCheckboxes values={consents} onChange={setConsents} error={consentError} />

        <div className="sm:col-span-2">
          <button data-testid="port-submit" disabled={loading} type="submit" className="rm-btn-primary disabled:opacity-60">
            {loading ? "Wysyłanie..." : "Złóż wniosek o przeniesienie"}
          </button>
        </div>
      </form>
      </SalesLockGate>
      </>
      )}
      <PagePlacements page="przenies-numer" />
    </div>
  );
}
